import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';

interface FantasyLeague {
  id: number;
  name: string;
  league_type: string;
  live_tournament_id: number;
  tournament_name: string;
  max_participants: number;
  current_participants: number;
  status: string;
  is_public: boolean;
  join_code?: string;
  created_by: number;
  created_at: string;
  is_creator: boolean;
}

const H2HPage: React.FC = () => {
  const [leagues, setLeagues] = useState<FantasyLeague[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuthStatus();
    fetchH2HLeagues();
  }, []);

  const checkAuthStatus = () => {
    const token = localStorage.getItem('authToken');
    setIsLoggedIn(!!token);
  };

  const fetchH2HLeagues = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await apiService.getFantasyLeagues();

      if (data.success) {
        const h2hLeagues = data.tournaments.filter(
          (league: FantasyLeague) => league.league_type === 'h2h'
        );
        setLeagues(h2hLeagues);
      } else {
        setError(data.message || 'Failed to load leagues');
      }
    } catch (err) {
      setError('Failed to load leagues');
      console.error('Error fetching H2H leagues:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLeague = () => {
    if (!isLoggedIn) {
      navigate('/tournaments');
      return;
    }
    navigate('/create-league?type=h2h');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto py-8 px-4">
      {/* Header */}
      <div className="text-center mb-12">
        <div className="text-6xl mb-4">⚔️</div>
        <h1 className="text-4xl font-bold text-white mb-4">Head-to-Head Tournaments</h1>
        <p className="text-xl text-white/80 max-w-3xl mx-auto">
          Face off against a single opponent in intense one-on-one battles! 
          Build your team and go head-to-head in match-based competitions.
        </p>
      </div>

      {/* How It Works */}
      <div className="bg-white/10 backdrop-blur-md rounded-lg p-8 border border-white/20 mb-8">
        <h2 className="text-2xl font-bold text-white mb-6">How H2H Works</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="bg-red-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">👤</span>
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">1. Find Opponent</h3>
            <p className="text-white/70">Challenge a friend or get matched with another player for a direct battle.</p>
          </div>
          <div className="text-center">
            <div className="bg-blue-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">🏏</span>
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">2. Build Team</h3>
            <p className="text-white/70">Select your players and create the best possible team for the match.</p>
          </div>
          <div className="text-center">
            <div className="bg-purple-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">🏆</span>
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">3. Battle & Win</h3>
            <p className="text-white/70">Your team's performance determines the winner. Only one can emerge victorious!</p>
          </div>
        </div>
      </div>

      {/* Match Types */}
      <div className="bg-white/10 backdrop-blur-md rounded-lg p-8 border border-white/20 mb-8">
        <h2 className="text-2xl font-bold text-white mb-6">H2H Match Types</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-semibold text-white mb-3">Single Match</h3>
            <ul className="space-y-2 text-white/80">
              <li>• <strong>One-off Battle:</strong> Single match competition</li>
              <li>• <strong>Quick Results:</strong> Winner determined after match ends</li>
              <li>• <strong>Instant Gratification:</strong> Immediate outcome</li>
              <li>• <strong>Perfect for:</strong> Quick challenges and testing strategies</li>
            </ul>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white mb-3">Series Battle</h3>
            <ul className="space-y-2 text-white/80">
              <li>• <strong>Multiple Matches:</strong> Best of 3, 5, or 7 matches</li>
              <li>• <strong>Consistency Test:</strong> Requires sustained performance</li>
              <li>• <strong>Strategy Depth:</strong> Adapt team selection across matches</li>
              <li>• <strong>Perfect for:</strong> Extended rivalries and tournaments</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Scoring & Rules */}
      <div className="bg-white/10 backdrop-blur-md rounded-lg p-8 border border-white/20 mb-8">
        <h2 className="text-2xl font-bold text-white mb-6">Scoring & Rules</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-semibold text-white mb-3">Team Building</h3>
            <ul className="space-y-2 text-white/80">
              <li>• <strong>Player Limit:</strong> 11 players per team</li>
              <li>• <strong>Position Balance:</strong> Minimum/maximum per position</li>
              <li>• <strong>Captain Selection:</strong> Double points for captain</li>
              <li>• <strong>Vice Captain:</strong> 1.5x points for vice captain</li>
            </ul>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white mb-3">Victory Conditions</h3>
            <ul className="space-y-2 text-white/80">
              <li>• <strong>Higher Score:</strong> Team with more points wins</li>
              <li>• <strong>Tie Breaker:</strong> Captain performance decides</li>
              <li>• <strong>Bonus Points:</strong> Milestones and achievements</li>
              <li>• <strong>Fair Play:</strong> No duplicate players between teams</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mb-8">
        <button
          onClick={handleCreateLeague}
          className="bg-gradient-to-r from-red-600 to-purple-600 hover:from-red-700 hover:to-purple-700 text-white px-4 sm:px-8 py-2 sm:py-3 rounded-lg text-sm sm:text-lg font-semibold transition-all transform hover:scale-105"
        >
          Create H2H League
        </button>
        <Link
          to="/tournaments"
          className="bg-white/10 hover:bg-white/20 text-white px-4 sm:px-8 py-2 sm:py-3 rounded-lg text-sm sm:text-lg font-semibold border border-white/20 transition-all text-center"
        >
          Browse All Tournaments
        </Link>
      </div>
    </div>
  );
};

export default H2HPage; 